import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import vuetify from '@/plugins/vuetify'
import {
  APPEARANCE_PRESETS,
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE_PRESET_ID,
  applyBrandThemeTokens,
  deriveBrandThemeTokens,
  getAppearancePresetById,
  isAppearancePresetId,
  type AppearancePresetId,
} from '@/utils/appearanceTheme'

const APPEARANCE_SCOPE_STORAGE_KEY = 'avicola_appearance_scope_mode_v1'
const APPEARANCE_SELF_PRESET_STORAGE_KEY = 'avicola_appearance_self_preset_v1'

export const useAppearanceStore = defineStore('appearance', () => {
  const selectedPresetId = ref<AppearancePresetId>(DEFAULT_APPEARANCE_PRESET_ID)
  const hydrated = ref(false)
  const scopeMode = ref<'client' | 'self'>('client')
  const selfPresetId = ref<AppearancePresetId | null>(null)

  const presets = APPEARANCE_PRESETS
  const currentPreset = computed(() => getAppearancePresetById(selectedPresetId.value))
  const currentTokens = computed(() => deriveBrandThemeTokens(currentPreset.value))
  const hasSelfOverride = computed(() => scopeMode.value === 'self' && !!selfPresetId.value)

  function applyCurrentTheme() {
    applyBrandThemeTokens(currentTokens.value, vuetify)
  }

  function setPreset(
    presetId: AppearancePresetId,
    options: { persistLocal?: boolean } = {}
  ) {
    const { persistLocal = true } = options
    selectedPresetId.value = getAppearancePresetById(presetId).id
    if (persistLocal) {
      localStorage.setItem(APPEARANCE_STORAGE_KEY, selectedPresetId.value)
    }
    applyCurrentTheme()
  }

  function resetToDefault(options?: { persistLocal?: boolean }) {
    setPreset(DEFAULT_APPEARANCE_PRESET_ID, options)
  }

  function setSelfOverridePreset(presetId: AppearancePresetId) {
    const normalizedPresetId = getAppearancePresetById(presetId).id
    scopeMode.value = 'self'
    selfPresetId.value = normalizedPresetId
    selectedPresetId.value = normalizedPresetId
    localStorage.setItem(APPEARANCE_SCOPE_STORAGE_KEY, 'self')
    localStorage.setItem(APPEARANCE_SELF_PRESET_STORAGE_KEY, normalizedPresetId)
    localStorage.setItem(APPEARANCE_STORAGE_KEY, normalizedPresetId)
    applyCurrentTheme()
  }

  function clearSelfOverride() {
    scopeMode.value = 'client'
    selfPresetId.value = null
    localStorage.setItem(APPEARANCE_SCOPE_STORAGE_KEY, 'client')
    localStorage.removeItem(APPEARANCE_SELF_PRESET_STORAGE_KEY)
  }

  function syncWithClientePreset(presetId?: string | null) {
    if (hasSelfOverride.value && selfPresetId.value) {
      selectedPresetId.value = selfPresetId.value
      applyCurrentTheme()
      return
    }

    const nextPresetId = isAppearancePresetId(presetId)
      ? presetId
      : DEFAULT_APPEARANCE_PRESET_ID

    scopeMode.value = 'client'
    selectedPresetId.value = nextPresetId
    localStorage.setItem(APPEARANCE_SCOPE_STORAGE_KEY, 'client')
    localStorage.setItem(APPEARANCE_STORAGE_KEY, nextPresetId)
    applyCurrentTheme()
  }

  function hydrate() {
    const savedPresetId = localStorage.getItem(APPEARANCE_STORAGE_KEY) as AppearancePresetId | null
    const savedScopeMode = localStorage.getItem(APPEARANCE_SCOPE_STORAGE_KEY)
    const savedSelfPresetId = localStorage.getItem(
      APPEARANCE_SELF_PRESET_STORAGE_KEY
    ) as AppearancePresetId | null

    scopeMode.value = savedScopeMode === 'self' ? 'self' : 'client'
    selfPresetId.value = isAppearancePresetId(savedSelfPresetId) ? savedSelfPresetId : null

    if (scopeMode.value === 'self' && selfPresetId.value) {
      selectedPresetId.value = selfPresetId.value
    } else {
      selectedPresetId.value = getAppearancePresetById(savedPresetId).id
    }

    applyCurrentTheme()
    hydrated.value = true
  }

  return {
    presets,
    hydrated,
    scopeMode,
    selfPresetId,
    hasSelfOverride,
    selectedPresetId,
    currentPreset,
    currentTokens,
    setPreset,
    resetToDefault,
    setSelfOverridePreset,
    clearSelfOverride,
    syncWithClientePreset,
    hydrate,
  }
})
